import { useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useAuth } from '../hooks/useAuth';
import { api, type CostRate } from '../lib/api';
import { fmtCurrency } from '../lib/fmt';
import {
  SectionLabel, Card, CardHeader, Table, Th, Tr, Td, ActionButton, Spinner, EmptyState,
} from '../components/ui';

// ─── Blended cost rate ──────────────────────────────────────────────────────
//
// What an hour of delivered person-time costs, blended across the team. ORP has
// always known what an hour earns; this is the other half, and every margin
// figure in the product divides by it.
//
// Anchored to the WORK DATE: a worklog is costed at the rate in force when the
// work happened, so setting this year's rate never restates last year's margin.
// Set one row per year (1 January) in the normal case; a mid-year revision is
// just another row.

const inputStyle: React.CSSProperties = {
  fontSize: 12, padding: '4px 8px',
  border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)',
  color: 'var(--color-text)', fontFamily: 'inherit',
};

/** 1 January of next year — the row an operator is most often here to add. */
function nextJan(): string {
  return `${new Date().getUTCFullYear() + 1}-01-01`;
}

export default function AdminCostRatesPage() {
  const { user } = useAuth();
  const canEdit = user?.permissions?.includes('admin_cost_rates') === true;
  const { data, isLoading, error, refetch } = useQuery(() => api.costRates.list(), []);

  const [effectiveFrom, setEffectiveFrom] = useState(nextJan());
  const [rate, setRate] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true); setErr(null);
    try {
      await api.costRates.save({
        effective_from: effectiveFrom,
        hourly_cost_rate: Number(rate),
        notes: notes.trim() || null,
      });
      setRate(''); setNotes('');
      refetch();
    } catch (e) {
      setErr((e as Error).message);
    } finally { setBusy(false); }
  }

  async function remove(r: CostRate) {
    if (!confirm(
      `Remove the blended cost rate effective ${r.effective_from}?\n\n`
      + 'Work on or after that date will fall back to the previous rate, or become '
      + 'uncosted if there is no earlier one. Every margin figure from that date '
      + 'forward changes.',
    )) return;
    setBusy(true); setErr(null);
    try { await api.costRates.remove(r.effective_from); refetch(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={error} />;
  const rates = data?.rates ?? [];

  return (
    <>
      <SectionLabel>Blended cost rate</SectionLabel>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 8, maxWidth: 720 }}>
        The fully-loaded cost of one hour of delivered time. Work is costed at the rate in force
        on the date it was logged, so adding this year’s rate never restates last year’s margin.
        Cost applies to <b>logged</b> hours, not just billable ones — non-billable time was paid
        for too.
      </div>

      {err && <div style={{ fontSize: 11, color: 'var(--color-red-text)', marginBottom: 8 }}>{err}</div>}

      {canEdit && (
        <Card>
          <CardHeader><span style={{ fontSize: 12, fontWeight: 500 }}>Add or correct a rate</span></CardHeader>
          <div style={{ padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Effective from</span>
              <input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} style={inputStyle} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Cost per hour</span>
              <input type="text" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="0.00" style={{ ...inputStyle, width: 120 }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 240 }}>
              <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
                Notes — how it was derived, who signed it off
              </span>
              <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} style={inputStyle} />
            </label>
            <ActionButton onClick={save} variant="primary" disabled={busy || !rate.trim()}>
              {busy ? 'Saving…' : 'Save rate'}
            </ActionButton>
          </div>
          <div style={{ padding: '0 16px 12px', fontSize: 10, color: 'var(--color-text-muted)' }}>
            Saving an existing effective date corrects that row rather than adding a second one.
          </div>
        </Card>
      )}

      <Card>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Schedule</span>
        </CardHeader>
        {rates.length === 0 ? (
          <EmptyState message="No cost rate set. Until one exists, margin cannot be calculated and the profitability view reports every hour as uncosted." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Effective from</Th>
                <Th right>Cost per hour</Th>
                <Th>Notes</Th>
                <Th>Last updated</Th>
                {canEdit && <Th>{''}</Th>}
              </tr>
            </thead>
            <tbody>
              {rates.map((r, i) => (
                <Tr key={r.effective_from}>
                  <Td bold>
                    {r.effective_from}
                    {/* The newest row is the one in force now — worth saying,
                        since the list is newest-first and that is easy to
                        misread as "most recently edited". */}
                    {i === 0 && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--color-green-text)' }}>in force</span>}
                  </Td>
                  <Td right>{fmtCurrency(r.hourly_cost_rate)}</Td>
                  <Td muted style={{ fontSize: 11 }}>{r.notes || '—'}</Td>
                  <Td muted style={{ fontSize: 11 }}>
                    {r.updated_at?.slice(0, 10)}{r.updated_by ? ` · ${r.updated_by}` : ''}
                  </Td>
                  {canEdit && (
                    <Td><ActionButton onClick={() => remove(r)} variant="danger" disabled={busy}>Remove</ActionButton></Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
